// Phase-2 Slice-22 — DDL detector hook.
//
// The query-execution pipeline (`useQueryExecution.ts:325`) already detects
// schema-changing DDL via `isRoutineDdl` / `isTableDdl` / `isSchemaDdl`; this
// module subscribes to that same hook by registering an index handle per
// (connection, database) and exposing a single
// `runIndexInvalidationForDdl(connectionId, sql)` entry point that the
// query-execution callback fires after a successful DDL query.
//
// EVERY index on the connection is refreshed, and a DROP is RE-VERIFIED
// against each database's catalog rather than deleting records blindly. The
// statement's database cannot be trusted to scope the refresh: a query tab's
// database is not the statement's once the batch says `USE other`, a
// connection configured without a database indexes the login default under
// `''`, and MySQL's catalog feed enumerates every schema into every index.
// Re-fetching is the one operation that is right in every case — the
// database that still holds the object re-indexes it, the one that lost it
// drops it (`buildForObject` fetches FIRST, then replaces the records: a null
// body is a confirmed absence, a failed fetch leaves the records untouched).
// Cost, per registration: object DDL runs one single-object body query per
// candidate identity (one for a schema-qualified name; for a bare name, one
// per candidate schema — implicit + held + probed — plus the one
// `listSchemasHoldingObject` probe); CONSTRAINT DDL is the expensive one — it
// re-pulls the WHOLE parseable listing of that database. MySQL, Oracle and
// SQLite hold a single registration per connection (see
// `navigationalLifecycle.ts`), so only SQL Server and PostgreSQL multiply this
// by the databases a user has searched.
//
// On a recognized DDL, per registration:
//   - DROP / CREATE / ALTER / OR REPLACE → `buildForObject` (single-object
//     refresh) for the kind(s) that fit. CREATE FUNCTION fans out across
//     `inline-tvf` + `scalar-function` since the SQL alone doesn't disclose
//     which catalog kind landed.
//
// Object identity is the catalog one the connection-wide feed emits —
// `schema.name`, never a database. Two normalisations bring a statement's
// name to it (`catalogIdentity`): a rename target that arrives
// database-qualified from the Object Explorer loses the database (so a
// sibling database's body is never recorded under another database's name),
// and a DDL name typed WITHOUT a schema (`CREATE VIEW my_view …`) gains the
// engine's implicit one (SQL Server `dbo`, PostgreSQL `public`, MySQL the
// database, Oracle the login's own schema, SQLite `main`) — the schema the
// listing keyed the object under. Without that the refresh misses the
// listing's record and files the object a second time under a bare key.
// Spelling is folded ONLY for a name that came out of a DDL statement
// (`ddlCatalogSpelling`, per component): a rename target and an index record
// are catalog identities already and are used as given.
// A bare name is ALSO re-verified under every schema the index already holds
// that name under (`candidateIdentities`) — a DROP has to reach the records
// the catalog no longer lists — and under every schema the CATALOG says holds
// that name and kind (`listSchemasHoldingObject`), which is how the hook
// learns where a bare CREATE actually ran (on MySQL / Oracle, whose listings
// enumerate every schema into the connection's one index, that includes a
// schema the statement never named). On a case-sensitive catalog (PostgreSQL, Oracle) `public.v` and a
// held `public."V"` are distinct candidates, both are refreshed, and the
// lifecycle gives those engines an index with case-EXACT object keys
// (the index is created with the per-kind case RESOLVER in production —
// `navLifecycleBuild.ts` passes `objectKeyCaseFor`; `{ caseSensitiveObjects: true }`
// is the unit-fixture shorthand) so refreshing
// one never touches the other's records.
//   - ALTER TABLE constraint ops → invalidate the connection's existing
//     check + default constraint records and re-fetch all of them. Slice 23's
//     connection-lifecycle build will eventually hold the same invariant; for
//     now the invalidator does it eagerly so find-references / rename stay
//     fresh between connect events.
//
// CREATE / ALTER PROCEDURE | TRIGGER invalidates the stale procedural records
// and re-indexes the new body via `buildForObject` (which routes procedural
// kinds through `fetchProceduralObjectBody` + `indexProceduralListing`), so
// find-references / rename stay consistent with the connection-wide build
// instead of going stale until reconnect. A DROP re-verifies rather than
// simply invalidating: `buildForObject` fetches FIRST, so an object a sibling
// database still holds keeps its records, and a failed read keeps them too —
// the removal happens only when the catalog answers that the object is gone.

import type { DatabaseEngineId } from '@/core/engines';
import { getCachedMysqlSchema } from '@/features/connections/services/language-services/mysqlSchemaCache';
import {
  type MaskOptions,
  mysqlMaskOptionsForConnection,
} from '@/features/workspace/common/utils/sqlTextMasker';

import type {
  IdentifierIndex,
  ObjectKind,
  ParseableObjectKind,
  PersistedObjectListing,
  QualifiedName,
  RenameKind,
} from '../types';

import { buildForObject } from './buildForObject';
import { classifyDdlForIndex, type DdlClassification } from './ddlClassifier';
import {
  indexCheckConstraintListing,
  indexDefaultConstraintListing,
} from './indexBuilder';
import { isParseableObjectKind, listParseableObjects, listSchemasHoldingObject } from './objectBodyFetcher';
import { catalogNameSpelling } from './engines/queryHelper';
import { objectKeyFor, type ObjectKeyCase } from './identifierIndex';

export type IndexInvalidatorRegistration = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly index: IdentifierIndex;
  /** "This index has no build in flight" — the lifecycle's hook
   *  (`connection-lifecycle/navLifecycleBuild.ts`), resolving at once when
   *  nothing is building and when the build in flight SETTLES otherwise.
   *  Every refresh below waits on it first: the lifecycle EMPTIES the index
   *  before it retries a failed build and the builder only records, so a
   *  refresh that writes into the emptied index is recorded a SECOND time by
   *  the build that follows — the object listed twice by every reader that
   *  does not dedupe. Deferred, not skipped: the build's catalog listing was
   *  fetched before this statement ran, so the build cannot carry the change
   *  and skipping would leave the object stale until the next DDL. Optional —
   *  a registration made without one (tests, the e2e seams) refreshes
   *  immediately, exactly as before. */
  readonly whenIdle?: () => Promise<void>;
  /** The SYNCHRONOUS half of `whenIdle`, for the ordering it cannot close:
   *  a refresh that started while nothing was building and is still awaiting
   *  its body fetch when a build BEGINS (the user retrying a failed one, which
   *  empties the index first). Checked immediately before every swap — while it
   *  answers `true` the refresh writes nothing and RE-QUEUES behind `whenIdle`,
   *  since the build that follows would otherwise record the same object a
   *  second time. Optional, and only meaningful next to `whenIdle`. */
  readonly isBuilding?: () => boolean;
  /** How many builds this index has STARTED. `isBuilding` cannot see a build
   *  that began AND finished inside one refresh's body fetch — the refresh
   *  reads `false` on both sides and installs a body older than the records
   *  the build just wrote. Captured before the fetch and re-read at the swap,
   *  a change means the same thing `isBuilding` does: write nothing, re-queue,
   *  re-read the catalog. */
  readonly buildGeneration?: () => number;
};

/** One registration per (connection, database) — the lifecycle registers one
 *  per index it holds: per database asked about on SQL Server / PostgreSQL,
 *  and exactly ONE per connection on MySQL / Oracle / SQLite, whose listings
 *  do not depend on the database named (`connection-lifecycle/databaseIdentity.ts`).
 *  The key keeps
 *  the database's exact spelling: the lifecycle has already decided which
 *  names are one identity, and PostgreSQL databases may legitimately differ
 *  only by case. */
const registry = new Map<string, IndexInvalidatorRegistration>();

function registryKey(connectionId: string, database: string): string {
  return `${connectionId}::${database}`;
}

/** Register the index handle for one (connection, database). The connection
 *  lifecycle calls this per database; tests call it directly. The returned
 *  disposer removes the registration so a re-connect/re-build cycle starts
 *  clean. */
export function registerDdlInvalidator(reg: IndexInvalidatorRegistration): () => void {
  const key = registryKey(reg.connectionId, reg.database);
  registry.set(key, reg);
  return () => {
    if (registry.get(key) === reg) registry.delete(key);
  };
}

/** Every index registered on `connectionId` — see the module header for why
 *  a DDL statement is never scoped to one database. */
function registrationsFor(connectionId: string): readonly IndexInvalidatorRegistration[] {
  return Array.from(registry.values()).filter((reg) => reg.connectionId === connectionId);
}

/** Fired by `ddlInvalidator.ts` (query editor + Table Designer) after a
 *  successful DDL query. No-op when the connection has no registered
 *  invalidator (e.g. mock connections).
 *
 *  `maskOptions` is the masking context that caller ALREADY captured for its
 *  own `isSchemaDdl` call, handed over because it captured it before dropping
 *  the MySQL snapshot the `sql_mode` lives in — see `mysqlSqlModeOptions`.
 *  Every other caller (tests, the e2e seams) omits it and the derivation below
 *  runs. */
export async function runIndexInvalidationForDdl(
  connectionId: string,
  sql: string,
  maskOptions?: MaskOptions,
): Promise<void> {
  const regs = registrationsFor(connectionId);
  if (regs.length === 0) return;
  // Classify ONCE per DDL with the connection's engine — the classifier's
  // string/comment masker reads `[`, `"`, `` ` ``, `$…$` and `q'…'` per the
  // engine's grammar (PostgreSQL `[` is an array constructor and `$$…$$` a
  // string, MySQL `"` is a string, Oracle `q'[…]'` is a string), and every
  // registration of a connection shares the engine. No registrations means
  // nothing to refresh, so the early return above also guarantees `regs[0]`.
  // MySQL's `"…"` and its backslash escapes are what the TEXT cannot decide —
  // under `ANSI_QUOTES` `DROP VIEW "v"` names a view rather than quoting a
  // string, and without `NO_BACKSLASH_ESCAPES` a `\'` inside `'…'` does not
  // close it. Both come off the session's `sql_mode` — see
  // `mysqlSqlModeOptions` for why the caller's captured value wins.
  const engineId = regs[0]!.engineId;
  const classified = classifyDdlForIndex(
    sql,
    engineId,
    engineId === 'mysql' ? mysqlSqlModeOptions(connectionId, maskOptions) : undefined,
  );
  if (!classified) return;
  // The ONE place a name is folded to catalog case: it came out of a DDL
  // statement. Rename targets and index records are catalog identities
  // already and are never folded. Every registration of a connection shares
  // the engine.
  const ddlName = ddlCatalogSpelling(classified.objectName, engineId, classified.schemaQuoted);
  // CONCURRENTLY, never in series: each registration waits for ITS OWN index's
  // build (`whenIdle`), and awaiting them one after another would park a
  // database whose index is idle behind one that is still building — a `master`
  // tab's `CREATE VIEW` sitting out the connect-time build of a large sibling
  // database, with Find References reading a stale index meanwhile. The
  // coalescing slot is per registration, so nothing here needs an order.
  await Promise.all(regs.map((reg) => refreshRegistrationForDdl(reg, classified, ddlName)));
}

/** One statement's refresh of one registration — BEHIND that registration's
 *  build, when it has a `whenIdle` hook (see the type). While a refresh is
 *  waiting, a later statement for the SAME object replaces it rather than
 *  queueing a second one: the refresh re-reads the catalog when it finally
 *  runs, so the last statement's outcome is what it would find either way, and
 *  running it twice only costs another round of body queries. A statement for a
 *  DIFFERENT object queues on its own — and a build that starts DURING the
 *  refresh puts it back in the same slot (see {@link runQueuedRefresh}). */
async function refreshRegistrationForDdl(
  reg: IndexInvalidatorRegistration,
  classified: DdlClassification,
  ddlName: QualifiedName,
): Promise<void> {
  const whenIdle = reg.whenIdle;
  // No hook, no build to wait for (tests, the e2e seams): run it now.
  if (!whenIdle) {
    // Registrations without a hook exist only in tests and the e2e seams (the
    // lifecycle always passes one — `navLifecycleBuild.ts`). Note this is the
    // FREEST overlap, not the absence of one: with no queue, two statements run
    // their candidates concurrently. The per-write tickets below order them.
    // Two things are missing rather than one: the coalescing, and the
    // `deferred` re-queue — a deferred swap here is dropped, not retried.
    // Both are harmless only because a registration with no `whenIdle` has no
    // build to defer behind.
    await applyDdlToCandidates(reg, classified, ddlName, refreshGuards(reg));
    return;
  }
  // A statement that will refresh NOTHING must not touch the slot: coalescing
  // replaces `latest`, so `ADD CHECK` followed by `ADD COLUMN spare integer`
  // on one table — while a build holds the slot open — left the slot carrying
  // the second statement, whose `constraintAffected` is false, and the
  // constraint listing was never fetched. The ticket rule has the same shape
  // one layer down (a no-op claims no ticket); this is the coalescing half.
  if (!refreshesAnything(classified)) return;
  const key = queuedRefreshKey(reg, classified, ddlName);
  const queued = queuedRefreshes.get(key);
  if (queued && queued.reg === reg) {
    queued.latest = { classified, ddlName };
    return queued.done;
  }
  // A slot left on this key by a registration THIS one replaced (a reconnect
  // under the same id) is waiting on an index nothing reads any more, and drops
  // itself at its staleness check. Take the key over instead of coalescing into
  // it — that would file this statement under a refresh that never runs.
  const slot: QueuedRefresh = { reg, latest: { classified, ddlName }, done: Promise.resolve() };
  queuedRefreshes.set(key, slot);
  slot.done = runQueuedRefresh(key, slot, whenIdle);
  return slot.done;
}

/** Whether this statement has any index write to do at all. A `table`
 *  statement only writes when it touched a constraint — `CREATE TABLE`,
 *  `DROP TABLE` and `ADD COLUMN` are upstream concerns (the sql-resolver
 *  schema cache), not this index's. Every other target kind refreshes its
 *  object. */
function refreshesAnything(classified: DdlClassification): boolean {
  return classified.targetKind !== 'table' || classified.constraintAffected === true;
}

/** A refresh waiting for its registration's index to stop building, plus the
 *  latest statement it is for (see {@link refreshRegistrationForDdl}). */
type QueuedRefresh = {
  readonly reg: IndexInvalidatorRegistration;
  latest: { readonly classified: DdlClassification; readonly ddlName: QualifiedName };
  /** Shared by every caller whose statement landed in this slot. */
  done: Promise<void>;
};

const queuedRefreshes = new Map<string, QueuedRefresh>();

/** The newest refresh STARTED for each WRITE — not for each statement.
 *
 *  A refresh frees its coalescing slot before fetching, so two refreshes of one
 *  object run at once and their swaps need ordering. The first version of this
 *  keyed the ticket by the STATEMENT's spelling, which is one level too high:
 *  the swap is keyed by what the INDEX holds, and the two differ in ways users
 *  hit —
 *
 *    - `CREATE VIEW my_view` and `CREATE VIEW public.my_view` are different
 *      statements and ONE index entry (typing the schema on a re-run is
 *      ordinary);
 *    - `dbo.V` and `dbo.v` are different spellings and one entry on a
 *      case-insensitive catalog;
 *    - a constraint refresh replaces EVERY constraint record in the
 *      registration, so two `ALTER TABLE`s on DIFFERENT tables write the same
 *      records;
 *    - a statement that refreshes NOTHING (`ADD COLUMN spare integer`) took a
 *      ticket anyway and could supersede a refresh that mattered.
 *
 *  So a ticket is claimed per WRITE, immediately before the fetch that feeds
 *  it, keyed exactly as the index keys the records it will replace
 *  ({@link objectKeyFor}). A statement that reaches no fetch claims nothing. */
const refreshTickets = new Map<string, number>();
let refreshTicketSeq = 0;

/** Claim `key` for this write and hand back the two things its caller needs:
 *  the check its swap must pass, and the release to run when it is done.
 *
 *  Released on completion so the map does not grow one entry per object ever
 *  refreshed — but only by the holder, so a newer claim that arrived meanwhile
 *  is never dropped. */
function takeRefreshTicket(key: string): { isSuperseded: () => boolean; release: () => void } {
  const ticket = ++refreshTicketSeq;
  refreshTickets.set(key, ticket);
  return {
    isSuperseded: () => refreshTickets.get(key) !== ticket,
    release: () => {
      if (refreshTickets.get(key) === ticket) refreshTickets.delete(key);
    },
  };
}

/** The key for ONE object write: the registration, plus the identity the index
 *  will file the records under — same fold, so every spelling that lands in one
 *  bucket takes one ticket. */
function objectRefreshKey(
  reg: IndexInvalidatorRegistration,
  kind: ParseableObjectKind,
  objectName: QualifiedName,
): string {
  // The index's OWN policy, with no fallback: `objectKeyCaseFor` always
  // answers (`identifierIndex.ts`'s `keyCaseFor` folds both components when a
  // kind's policy is unknown, and the lifecycle's gate means a live
  // registration's is not). The ticket must fold exactly as the index does,
  // because the BUCKET is the unit of the swap: when two spellings share a
  // bucket, `invalidate` on either clears it, so an exact ticket would not
  // keep one of them — it would only stop the two refreshes ordering and let
  // the older read erase the newer, which is the race tickets exist to close.
  const policy = reg.index.objectKeyCaseFor(kind);
  return `${registryKey(reg.connectionId, reg.database)}::${objectKeyFor(kind, objectName, policy)}`;
}

/** The key for the constraint write, which replaces every check/default record
 *  in the registration — so it is registration-wide, not per table. */
function constraintRefreshKey(reg: IndexInvalidatorRegistration): string {
  return `${registryKey(reg.connectionId, reg.database)}::__constraints__`;
}

/** Run one `buildForObject` under its own ticket. */
async function buildObjectOrdered(
  reg: IndexInvalidatorRegistration,
  kind: ParseableObjectKind,
  objectName: QualifiedName,
  guards: RefreshGuards,
): Promise<void> {
  // Liveness BEFORE the claim: the ticket key is shared with whatever
  // registration currently holds this (connection, database), and a reconnect
  // under the same id installs a new one. A stale refresh that reached here
  // through a swallowed probe rejection would otherwise claim the key and
  // supersede the LIVE registration's work — which then writes nothing, while
  // the stale refresh is refused at its own swap, leaving the live index
  // stale until the next DDL.
  if (guards.isStale()) return;
  const ticket = takeRefreshTicket(objectRefreshKey(reg, kind, objectName));
  try {
    await buildForObject(
      {
        ...sharedFetchOpts(reg),
        objectKind: kind,
        objectName,
        ...guardOpts(guards),
        isSuperseded: ticket.isSuperseded,
      },
      reg.index,
    ).catch(() => 'failed' as const);
  } finally {
    ticket.release();
  }
}

/** One slot per (registration, DDL target) — the spelling is the catalog one
 *  already, and is compared EXACTLY: on a case-sensitive catalog `public.v`
 *  and `public."V"` are different objects and must not share a slot. */
function queuedRefreshKey(
  reg: IndexInvalidatorRegistration,
  classified: DdlClassification,
  ddlName: QualifiedName,
): string {
  return `${registryKey(reg.connectionId, reg.database)}::${classified.targetKind}::${
    ddlName.schema ?? ''
  }::${ddlName.name}`;
}

async function runQueuedRefresh(
  key: string,
  slot: QueuedRefresh,
  whenIdle: () => Promise<void>,
): Promise<void> {
  const reg = slot.reg;
  for (;;) {
    // SETTLED, not succeeded: a FAILED build keeps everything it recorded (the
    // index is marked partial, not emptied), so this refresh still has records to
    // correct — and a cancelled one has torn the registration down, which the
    // staleness check below catches.
    await whenIdle().catch(() => undefined);
    const { classified, ddlName } = slot.latest;
    // Free the slot BEFORE the refresh runs: a statement that arrives from here
    // on is about records this refresh is already writing, so it queues its own.
    if (queuedRefreshes.get(key) === slot) queuedRefreshes.delete(key);
    if (isRegStale(reg)) return;
    // No statement-level ticket: supersession is claimed per WRITE, inside
    // `buildObjectOrdered` / the constraint rebuild, because that is the
    // granularity the swap actually replaces records at.
    const guards = refreshGuards(reg);
    await applyDdlToCandidates(reg, classified, ddlName, guards);
    if (!guards.deferred) return;
    // A build STARTED (or started AND finished) while this refresh was
    // fetching, so at least one swap of this run was refused. NOT "nothing was
    // written": a statement with several candidates may have completed earlier
    // swaps before the build began, and a later candidate's fetch may have
    // started after it ended. The re-run is a conservative REPLAY of the whole
    // refresh — every candidate re-read, some of them redundantly — which is
    // idempotent because each swap replaces its own records wholesale. Take
    // the slot back and
    // wait it out — unless a later statement for the same target already holds
    // the key, whose own refresh re-reads the catalog and so covers this one.
    if (queuedRefreshes.has(key)) return;
    queuedRefreshes.set(key, slot);
  }
}

/** The two "do not write" checks every `buildForObject` of ONE refresh run
 *  shares, plus what the run has to report back: `deferred` records that a swap
 *  was skipped because a build had started, so {@link runQueuedRefresh} knows
 *  to re-queue rather than treat the refresh as applied. */
type RefreshGuards = {
  readonly isStale: () => boolean;
  readonly isBuilding: () => boolean;
  deferred: boolean;
};

function refreshGuards(reg: IndexInvalidatorRegistration): RefreshGuards {
  // Captured when the guards are made — before any fetch this refresh runs.
  const generationAtStart = reg.buildGeneration?.();
  const guards: RefreshGuards = {
    isStale: () => isRegStale(reg),
    isBuilding: () => {
      // A build running NOW, or one that started and finished while this
      // refresh was fetching: both mean the records this refresh would write
      // are older than what the index already holds.
      const rebuilt = generationAtStart !== undefined
        && reg.buildGeneration?.() !== generationAtStart;
      if (reg.isBuilding?.() !== true && !rebuilt) return false;
      guards.deferred = true;
      return true;
    },
    deferred: false,
  };
  return guards;
}

/** Every catalog identity this statement's name may denote in `reg`'s index,
 *  refreshed in turn. */
async function applyDdlToCandidates(
  reg: IndexInvalidatorRegistration,
  classified: DdlClassification,
  ddlName: QualifiedName,
  guards: RefreshGuards,
): Promise<void> {
  // Constraint DDL is table-scoped and rebuilt wholesale — one pass.
  const candidates =
    classified.targetKind === 'table'
      ? [catalogIdentity(ddlName, reg)]
      : await candidateIdentities(
          ddlName,
          classified.objectName,
          reg,
          refreshedKindsFor(classified.targetKind),
        );
  for (const objectName of candidates) {
    await applyDdlToRegistration(reg, classified, objectName, guards);
  }
}

/**
 * The two MySQL `sql_mode` settings the classifier's masker needs, for a
 * connection whose registrations say `mysql`.
 *
 * `captured` WINS when the caller took one: `ddlInvalidator.invalidateAfterDdl`
 * reads `sql_mode` off the MySQL IntelliSense snapshot for its own
 * `isSchemaDdl` call and then DROPS that snapshot (`dropMysqlSchema`, so the
 * next ensure is a real refresh) before firing this hook — deriving it again
 * here reads a missing `sqlMode`, falls back to the server default, and masks
 * the target of a successful `DROP VIEW "v"` as a string, leaving the dropped
 * view indexed.
 *
 * Without one (unit tests, the e2e seams, any future caller that has not
 * dropped anything) the derivation below is the right source — the same one
 * `ddlInvalidator.ts` reads, so the two maskers never disagree. It is NOT just
 * the cached snapshot: a connection whose snapshot a PREVIOUS invalidation
 * dropped still answers from the mode remembered for it
 * (`mysqlMaskOptionsForConnection`), which is what keeps a second
 * `DROP VIEW "v2"` under `ANSI_QUOTES` classified while nothing has rebuilt the
 * snapshot yet. Only a connection nothing ever observed means server defaults.
 */
function mysqlSqlModeOptions(
  connectionId: string,
  captured?: MaskOptions,
): Omit<MaskOptions, 'engine'> {
  if (captured?.engine === 'mysql') return captured;
  return mysqlMaskOptionsForConnection(connectionId, getCachedMysqlSchema(connectionId)?.sqlMode);
}

/** The index kinds a DDL target refreshes — the function fan-out spans two. */
function refreshedKindsFor(targetKind: DdlClassification['targetKind']): readonly ObjectKind[] {
  switch (targetKind) {
    case 'view':
      return ['view'];
    case 'function':
      return ['inline-tvf', 'scalar-function'];
    case 'procedure':
      return ['procedure'];
    case 'trigger':
      return ['trigger'];
    case 'table':
      return [];
  }
}

/** The catalog identities a statement's name may denote in `reg`'s index: a
 *  schema-qualified name is one; a bare name is the engine's implicit schema
 *  PLUS every schema the index already holds that name under (as recorded)
 *  PLUS every schema the catalog says holds it (see the module header).
 *  `spelled` is the DDL name in catalog case; `typed` is how the statement
 *  spelled it (the probe folds it itself). */
async function candidateIdentities(
  spelled: QualifiedName,
  typed: QualifiedName,
  reg: IndexInvalidatorRegistration,
  kinds: readonly ObjectKind[],
): Promise<readonly QualifiedName[]> {
  if (spelled.schema) return [catalogIdentity(spelled, reg)];
  // On a case-sensitive catalog (PostgreSQL, Oracle) `public.v` and
  // `public."V"` are different objects and both get refreshed; elsewhere they
  // are one object and collapse. MySQL decides per component.
  const keyCase = catalogKeyCase(reg, kinds);
  const byKey = new Map<string, QualifiedName>();
  const keyOf = (identity: QualifiedName): string => {
    const schema = keyCase.schema ? (identity.schema ?? '') : (identity.schema ?? '').toLowerCase();
    const name = keyCase.name ? identity.name : (identity.foldedName ?? identity.name).toLowerCase();
    return `${schema}::${name}`;
  };
  const add = (identity: QualifiedName): void => {
    if (!identity.schema) return;
    const key = keyOf(identity);
    if (!byKey.has(key)) byKey.set(key, identity);
  };
  const implicit = implicitSchemaFor(reg.engineId, reg.database);
  if (implicit) add(withSchema(spelled, implicit));
  const leaf = (spelled.foldedName ?? spelled.name).toLowerCase();
  for (const kind of kinds) {
    for (const held of reg.index.listObjectsOfKind(kind)) {
      if (held.schema && (held.foldedName ?? held.name).toLowerCase() === leaf) add(held);
    }
  }
  try {
    const probed = await listSchemasHoldingObject({
      connectionId: reg.connectionId,
      database: reg.database,
      engineId: reg.engineId,
      name: typed.name,
      quoted: typed.quoted,
      kinds,
    });
    for (const schema of probed) add(withSchema(spelled, schema));
  } catch {
    // The catalog could not be asked — the schemas above are still refreshed.
  }
  if (byKey.size === 0) return [spelled];
  return Array.from(byKey.values());
}

/** The SAME rule the lifecycle keys this registration's index by
 *  (`IdentifierIndex.objectKeyCaseFor`), so a candidate that dedupes away here could not have
 *  addressed its own bucket anyway: PostgreSQL and Oracle store a delimited
 *  identifier case-exactly and treat `v` and `"V"` as different objects; SQL
 *  Server follows the DATABASE's collation; MySQL decides per kind AND per
 *  name component; SQLite compares case-insensitively. A refresh spans several
 *  kinds at once (a `CREATE FUNCTION` covers both function kinds), so a
 *  component is kept exact only when EVERY kind in play keeps it — and a kind
 *  whose policy is not known yet folds, like the index does. */
function catalogKeyCase(reg: IndexInvalidatorRegistration, kinds: readonly ObjectKind[]): ObjectKeyCase {
  // The policy the index FROZE for each kind (or, before its first record,
  // the resolver's current answer) — read off the handle, never recomputed.
  const perKind = kinds.map((kind) => reg.index.objectKeyCaseFor(kind));
  return {
    schema: perKind.length > 0 && perKind.every((p) => p?.schema === true),
    name: perKind.length > 0 && perKind.every((p) => p?.name === true),
  };
}

/** A DDL statement's name in the engine's catalog case — each component
 *  folded unless the statement delimited it (`"Sales".v` keeps `Sales`, folds
 *  `v`; `Sales."V"` folds `Sales`, keeps `V`). */
function ddlCatalogSpelling(name: QualifiedName, engineId: DatabaseEngineId, schemaQuoted: boolean): QualifiedName {
  const spelledName = catalogNameSpelling(engineId, name.name, name.quoted);
  const spelledSchema = name.schema ? catalogNameSpelling(engineId, name.schema, schemaQuoted) : undefined;
  return {
    ...name,
    name: spelledName,
    schema: spelledSchema,
    raw: spelledSchema ? `${spelledSchema}.${spelledName}` : spelledName,
  };
}

async function applyDdlToRegistration(
  reg: IndexInvalidatorRegistration,
  classified: DdlClassification,
  objectName: QualifiedName,
  guards: RefreshGuards,
): Promise<void> {
  if (classified.targetKind === 'view') {
    await dispatchSingleKind(reg, objectName, 'view', guards);
    return;
  }

  if (classified.targetKind === 'function') {
    // The SQL alone doesn't tell us if the catalog landed an inline TVF or a
    // scalar SQL function (or a procedural body). Refresh both kinds —
    // `buildForObject` reports `absent` for the kind the catalog doesn't
    // match (and for a dropped object in this database).
    await Promise.all([
      buildObjectOrdered(reg, 'inline-tvf', objectName, guards),
      buildObjectOrdered(reg, 'scalar-function', objectName, guards),
    ]);
    return;
  }

  if (classified.targetKind === 'procedure' || classified.targetKind === 'trigger') {
    // Re-pull the body so procedural find-references / rename stay consistent
    // with the connection-wide build (a dropped body re-pulls as null →
    // records removed).
    await dispatchSingleKind(reg, objectName, classified.targetKind, guards);
    return;
  }

  if (classified.targetKind === 'table') {
    if (classified.constraintAffected) {
      await rebuildAllConstraints(reg, guards);
    }
    // Plain CREATE / DROP TABLE doesn't touch the parseable-object index;
    // table-level introspection is upstream (sql-resolver schema cache).
    return;
  }
}

/** A parsed object whose body the rename executor successfully rewrote (its
 *  `ALTER … AS <newBody>` ran). NAV-3: the executor applies these ALTERs
 *  directly via `executeQueryV2`, bypassing the `useQueryExecution.ts` DDL
 *  hook, so the index must be refreshed here instead. */
export type RenameRefreshedObject = {
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
};

/** The renamed schema-side object's identity. Only `'view'` is itself a
 *  parsed-object kind whose own body records are keyed by the renamed name;
 *  `'table'` / `'column'` renames only mutate *referencing* bodies (covered
 *  by `appliedBodyObjects`). */
export type RenameRefreshTarget = {
  readonly kind: RenameKind;
  readonly fromIdentifier: QualifiedName;
  readonly toIdentifier: QualifiedName;
};

export type RenameRefreshInput = {
  readonly target: RenameRefreshTarget;
  /** Objects whose body-rewrite ALTER *successfully* applied. A partial
   *  failure passes only the successfully-applied subset so the index is
   *  never left half-updated for an object whose ALTER never landed. */
  readonly appliedBodyObjects: readonly RenameRefreshedObject[];
};

/** NAV-3 — refresh the parsed-object index after a rename apply.
 *
 *  `renameExecutor` runs the schema ALTER + per-object body-rewrite ALTERs
 *  directly through `executeQueryV2`, which never triggers the
 *  `useQueryExecution.ts` → `runIndexInvalidationForDdl` hook. Without this
 *  the in-memory index keeps the OLD identifier + OLD bodies until reconnect.
 *
 *  For every successfully-rewritten body object: re-pull + re-index the new
 *  body (so refs reflect the renamed identifier). For a `'view'` self-rename:
 *  re-verify BOTH the old and the new name — in the renamed database the old
 *  name is gone and the new one carries the body; a sibling database may
 *  hold an unrelated object under either name and keeps it. Identities are
 *  normalised to the catalog's (`catalogIdentity`).
 *
 *  No-op when the connection has no registered index (mock connections / a
 *  unit test that didn't register one) — same contract as
 *  `runIndexInvalidationForDdl`. */
export async function runIndexRefreshForRename(
  connectionId: string,
  input: RenameRefreshInput,
): Promise<void> {
  // Concurrently, for the reason `runIndexInvalidationForDdl` is: each
  // registration waits for its own index's build and none of them needs to
  // wait for another's.
  await Promise.all(
    registrationsFor(connectionId).map((reg) => refreshRegistrationForRename(reg, input)),
  );
}

async function refreshRegistrationForRename(
  reg: IndexInvalidatorRegistration,
  input: RenameRefreshInput,
): Promise<void> {
  // Behind the registration's build, for the reason the DDL hook is (see
  // `IndexInvalidatorRegistration.whenIdle`): these `buildForObject` calls
  // record into the same index a retry has just emptied. No coalescing slot
  // here — NOT because a rename is "one user gesture at a time", which the
  // per-write tickets below exist precisely because it is not (two Undo
  // commands overlap; a query-tab ALTER is a second gesture) — but because
  // coalescing is an economy and the tickets are the correctness guard. A
  // build that starts mid-refresh (`isBuilding`) is waited out and the refresh
  // re-run, as on the DDL path.
  for (;;) {
    await reg.whenIdle?.().catch(() => undefined);
    if (isRegStale(reg)) return;
    // The per-WRITE tickets inside `buildObjectOrdered` order this path too —
    // against another rename (two Undo commands can overlap, because
    // `undoLastRename` pops its entry before awaiting the apply) and against an
    // ordinary DDL refresh of the same object from a query tab or the Table
    // Designer. Neither is arbitrated by "one user gesture at a time".
    const guards = refreshGuards(reg);
    await applyRenameToRegistration(reg, input, guards);
    if (!guards.deferred || !reg.whenIdle) return;
  }
}

async function applyRenameToRegistration(
  reg: IndexInvalidatorRegistration,
  input: RenameRefreshInput,
  guards: RefreshGuards,
): Promise<void> {
  // Refresh every successfully-rewritten referencing body in place — through
  // the same per-write ticket the DDL path uses, so a rename orders against an
  // overlapping DDL refresh of the SAME object and against another rename.
  // Neither is arbitrated by "one user gesture at a time": `undoLastRename`
  // pops its entry before awaiting the apply, so two Undo commands overlap,
  // and a query-tab or Table-Designer ALTER is a second gesture entirely.
  //
  // CONSTRAINT bodies are the exception, and the ticket alone does not order
  // them: a rename rewrites ONE constraint, but the DDL path's constraint
  // refresh replaces EVERY constraint record under the registration-wide
  // `__constraints__` key, so a per-object ticket and that one never meet and
  // the older read wins whichever way round they land. The unit of the swap is
  // the registration for constraints — which is this design's own rule — so a
  // rename that rewrote any constraint body re-reads the whole listing under
  // that same key. It costs one listing fetch, exactly what `ADD CHECK`
  // already pays. (The alternative, making the single write consult the wide
  // ticket, is unsound both ways: refusing the wide listing would drop the
  // OTHER constraints it carries, and refusing the single write is only safe
  // if the listing is guaranteed to land, which `onIncomplete` says it is not.)
  let renamedAConstraint = false;
  for (const obj of input.appliedBodyObjects) {
    if (!isParseableObjectKind(obj.objectKind)) continue;
    if (obj.objectKind === 'check-constraint' || obj.objectKind === 'default-constraint') {
      renamedAConstraint = true;
      continue;
    }
    await buildObjectOrdered(reg, obj.objectKind, catalogIdentity(obj.objectName, reg), guards);
  }
  if (renamedAConstraint) await rebuildAllConstraints(reg, guards);

  // A view self-rename moves the view's OWN body records: re-verify the old
  // name (gone where the rename ran, kept where a sibling holds one) and the
  // new name. (Table/column renames don't index the schema-side object itself
  // — only its references, handled above.) NAV-5: `buildForObject` fetches
  // before it touches the index and honours `isStale`, so a disconnect
  // mid-fetch never writes into a torn-down index.
  if (input.target.kind === 'view') {
    for (const name of [input.target.fromIdentifier, input.target.toIdentifier]) {
      await buildObjectOrdered(reg, 'view', catalogIdentity(name, reg), guards);
    }
  }
}

/** The identity the index keys objects by: what the connection-wide catalog
 *  feed emits (`makeQualifiedName` in `engines/queryHelper.ts`) — schema and
 *  name, never a database. A database-qualified name loses the database; a
 *  schema-less one gains the engine's implicit schema (see the header). */
function catalogIdentity(name: QualifiedName, reg: IndexInvalidatorRegistration): QualifiedName {
  if (name.schema) return withSchema(name, name.schema, name.foldedSchema);
  return withSchema(name, implicitSchemaFor(reg.engineId, reg.database));
}

/** `name` re-keyed under `schema` (database dropped), folded the way the
 *  listing folds a catalog schema unless the caller already folded it. */
function withSchema(name: QualifiedName, schema: string | undefined, foldedSchema?: string): QualifiedName {
  return {
    name: name.name,
    schema,
    raw: schema ? `${schema}.${name.name}` : name.name,
    quoted: name.quoted,
    foldedName: name.foldedName,
    foldedSchema: foldedSchema ?? schema?.toLowerCase(),
  };
}

/** The schema a statement's bare object name lands in — spelled as the
 *  engine's listing spells it (`s.name` / `nspname` / `ROUTINE_SCHEMA` /
 *  `OWNER` / the SQLite feed's fixed `'main'`). */
function implicitSchemaFor(engineId: DatabaseEngineId, database: string): string | undefined {
  switch (engineId) {
    case 'mssql':
      return 'dbo';
    case 'pgsql':
      return 'public';
    case 'mysql':
      // A MySQL "schema" IS the database the statement ran in.
      return database || undefined;
    case 'oracle':
      // The `database` of an Oracle registration is the schema the app
      // scopes the connection to (the login's own); `ALL_*.OWNER` is upper.
      return database ? database.toUpperCase() : undefined;
    case 'sqlite':
      return 'main';
    default:
      return undefined;
  }
}

async function dispatchSingleKind(
  reg: IndexInvalidatorRegistration,
  objectName: QualifiedName,
  parseableKind: ParseableObjectKind,
  guards: RefreshGuards,
): Promise<void> {
  // A DROP re-verifies too: this registration may be a sibling database that
  // still holds the object (see the module header). `buildForObject` swaps the
  // records only after a successful fetch.
  await buildObjectOrdered(reg, parseableKind, objectName, guards);
}

/** The guards as `buildForObject` takes them — one object per call site so the
 *  `deferred` flag above is raised by whichever swap is skipped. */
function guardOpts(guards: RefreshGuards): {
  readonly isStale: () => boolean;
  readonly isBuilding: () => boolean;
} {
  return { isStale: guards.isStale, isBuilding: guards.isBuilding };
}

function sharedFetchOpts(reg: IndexInvalidatorRegistration): {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
} {
  return { connectionId: reg.connectionId, database: reg.database, engineId: reg.engineId };
}

/** NAV-5: true once this registration is no longer the live one for its
 *  (connection, database) (disconnect/teardown removed or replaced it).
 *  Mirrors the identity check the `registerDdlInvalidator` disposer uses. */
function isRegStale(reg: IndexInvalidatorRegistration): boolean {
  return registry.get(registryKey(reg.connectionId, reg.database)) !== reg;
}

/** Rebuild every check + default constraint record on the connection. v1
 *  scope: doing per-table targeted rebuilds requires per-engine table-scoped
 *  introspection queries we don't have yet, so the safer path is a connection-
 *  wide constraint refresh. Its cost is NOT bounded by the constraint count:
 *  the listing it fetches is the whole parseable feed, view and function
 *  bodies included, so an earlier claim here that it "finishes well inside the
 *  300 ms budget" was asserting a benchmark nobody had run. Removed rather
 *  than re-stated — the honest description is that this is a whole-database
 *  read, taken deliberately because per-table targeted rebuilds would need
 *  per-engine table-scoped introspection this branch does not have.
 *
 *  NAV-5: fetch BEFORE invalidating. The prior `invalidate → await → repopulate`
 *  ordering left an empty window during the await in which a synchronous
 *  find-references call returned a false "no references". Fetching first, then
 *  doing invalidate→repopulate with NO await between, makes the swap atomic to
 *  any synchronous reader. DROP CONSTRAINT still cleans up: a dropped constraint
 *  won't be present in `listings`, so it is absent after the swap. */
async function rebuildAllConstraints(
  reg: IndexInvalidatorRegistration,
  guards: RefreshGuards,
): Promise<void> {
  // Claimed BEFORE the listing fetch below, and released when the swap is
  // done or abandoned.
  if (guards.isStale()) return;
  const constraintTicket = takeRefreshTicket(constraintRefreshKey(reg));
  try {
    await rebuildAllConstraintsUnderTicket(reg, guards, constraintTicket);
  } finally {
    constraintTicket.release();
  }
}

async function rebuildAllConstraintsUnderTicket(
  reg: IndexInvalidatorRegistration,
  guards: RefreshGuards,
  constraintTicket: { isSuperseded: () => boolean },
): Promise<void> {
  // A listing that lost a page is not authoritative — replacing every
  // constraint record from it would silently drop the ones the lost page
  // held (on this database or, refresh-all being what it is, on a sibling
  // whose catalog read failed). Keep what we have instead.
  //
  // DIVERGENCE from `buildIndexForConnection`, which REJECTS on the failure
  // half of this signal (`onFetchFailed`) so the lifecycle can fail readiness
  // and rebuild: this path has no readiness to fail and nothing rebuilds it —
  // it is a fire-and-forget hook on a DDL statement. Both reasons therefore
  // land on the same conservative answer, `onIncomplete`: don't swap, so the
  // complete records already in the index survive the failed refresh (stale at
  // worst, where a partial swap would be a false "no references"). Marking the
  // index partial here would be wrong for the opposite reason — the flag is
  // sticky until a rebuild that nothing here schedules.
  let incomplete = false;
  const listings = await listParseableObjects({
    ...sharedFetchOpts(reg),
    onIncomplete: () => {
      incomplete = true;
    },
  }).catch(() => {
    incomplete = true;
    return [] as readonly PersistedObjectListing[];
  });
  if (incomplete) return;
  // NAV-5: connection torn down while the fetch was in flight → its index is
  // detached/cleared; don't invalidate or write the stale result back.
  if (guards.isStale()) return;
  // …and a connection-wide build that started while the listing was in flight
  // will record these same constraints itself. Nothing is written; the caller
  // re-queues behind it (see `refreshGuards`).
  if (guards.isBuilding()) return;
  // …and a LATER constraint refresh may have started while this listing was in
  // flight. The ticket is registration-WIDE because this swap replaces every
  // check/default record in the registration: two `ALTER TABLE`s on DIFFERENT
  // tables write the same records, so a per-table ticket would let the older
  // listing erase the newer table's constraints.
  if (constraintTicket.isSuperseded()) return;
  // Synchronous swap — no await between invalidate and repopulate so
  // find-references never sees a false-empty window.
  invalidateAll(reg.index, 'check-constraint');
  invalidateAll(reg.index, 'default-constraint');
  for (const listing of listings) {
    if (listing.objectKind === 'check-constraint') indexCheckConstraintListing(listing, reg.index);
    else if (listing.objectKind === 'default-constraint') indexDefaultConstraintListing(listing, reg.index);
  }
}

function invalidateAll(index: IdentifierIndex, kind: ObjectKind): void {
  for (const name of index.listObjectsOfKind(kind)) index.invalidate(kind, name);
}

/** Test-only — wipe the registry between unit cases. */
export function __resetIndexInvalidatorForTest(): void {
  registry.clear();
  queuedRefreshes.clear();
  refreshTickets.clear();
}

/** Test-only — the registration keys (`connectionId::database`) currently held. */
export function __indexInvalidatorRegistrationsForTest(): readonly string[] {
  return Array.from(registry.keys());
}

// Re-export the classifier so callers can import a single namespace.
export { classifyDdlForIndex } from './ddlClassifier';
export type { DdlClassification, DdlOperation, DdlTargetKind } from './ddlClassifier';

// `QualifiedName` re-export for ergonomic test imports.
export type { QualifiedName };
